/**
 * 플레이 씬. 맵 1개를 담당하고, 포탈 이동 시 같은 씬을 새 데이터로 다시 시작한다.
 */

import Phaser from 'phaser';
import { ANIM } from '../assets/animations';
import type { AssetTable } from '../assets/loader';
import { bgm } from '../audio/Bgm';
import { Sfx } from '../audio/Sfx';
import {
  COMBAT,
  DROPS,
  NET,
  PLAYER,
  PROGRESSION,
  RESPAWN,
  WORLD,
  type SkillDef,
} from '../data/balance';
import { ITEMS } from '../data/items';
import { MAPS, type MapDef } from '../data/maps';
import { getTitle } from '../data/titles';
import type { StatusInflict } from '../data/monsters';
import { DEPTH } from '../game/depth';
import { EV, type StatusTickPayload } from '../game/events';
import { Controls } from '../input/Controls';
import { Drop } from '../entities/Drop';
import { Monster } from '../entities/Monster';
import { MonsterShot } from '../entities/MonsterShot';
import { Npc } from '../entities/Npc';
import { PlayerShot } from '../entities/PlayerShot';
import { Player, PLAYER_EVENT } from '../entities/Player';
import { RemotePlayer } from '../entities/RemotePlayer';
import { net } from '../net/NetClient';
import { DamageNumbers } from '../systems/DamageNumbers';
import type { PlayerState } from '../systems/PlayerState';
import { SaveGame } from '../systems/SaveGame';
import { SpawnDirector } from '../systems/SpawnDirector';
import { buildTerrain, type Terrain } from '../systems/TerrainBuilder';
import { ComboTracker } from '../systems/Combo';
import { replay } from '../systems/Replay';
import { settings } from '../systems/Settings';
import { training } from '../systems/Training';
import { ComboMeter } from '../ui/ComboMeter';
import { rollMonsterDamage, rollPlayerDamage, rollHit } from '../systems/combat';
import { applyWeaponToSkill } from '../systems/stats';
import { TEXT_COLOR, UI_FONT } from '../ui/theme';
import { REG } from './BootScene';
import { HudScene } from './HudScene';

interface PlaySceneData {
  mapId: string;
  spawnId: string;
}

export class PlayScene extends Phaser.Scene {
  private assets!: AssetTable;
  private state!: PlayerState;
  private controls!: Controls;
  private map!: MapDef;
  private terrain!: Terrain;
  private player!: Player;
  private spawner!: SpawnDirector;
  private damageNumbers!: DamageNumbers;
  private drops!: Phaser.Physics.Arcade.Group;
  private shots!: Phaser.Physics.Arcade.Group;
  private playerShots!: Phaser.Physics.Arcade.Group;
  private hpBars!: Phaser.GameObjects.Graphics;
  private attackId = 0;
  private saveTimer = 0;
  private transitioning = false;
  private deathTimer = 0;
  private deathPromptShown = false;
  private deathLostExp = 0;
  private spawnPoint = { x: 0, y: 0 };
  private actionHint!: Phaser.GameObjects.Text;
  private nameTag!: Phaser.GameObjects.Text;
  private titleTag!: Phaser.GameObjects.Text;
  private npcs: Npc[] = [];
  private readonly remotes = new Map<string, RemotePlayer>();
  private readonly combo = new ComboTracker();
  private comboMeter!: ComboMeter;

  constructor() {
    super('play');
  }

  create(data: PlaySceneData): void {
    this.assets = this.registry.get(REG.assets) as AssetTable;
    this.state = this.registry.get(REG.state) as PlayerState;
    this.controls = new Controls(this);
    this.attackId = 0;
    this.transitioning = false;
    this.deathTimer = 0;
    this.deathPromptShown = false;
    this.deathLostExp = 0;

    this.map = MAPS[data.mapId] ?? MAPS.henesys;
    this.state.mapId = this.map.id;
    this.state.spawnId = data.spawnId;

    this.physics.world.gravity.y = WORLD.gravity;
    this.physics.world.setBounds(0, 0, this.map.width, this.map.height);
    this.cameras.main.setBounds(0, 0, this.map.width, this.map.height);
    this.cameras.main.setBackgroundColor(this.map.palette.skyTop);

    this.terrain = buildTerrain(this, this.map, this.assets);

    const spawn = this.map.spawns[data.spawnId] ?? this.map.spawns.start;
    this.spawnPoint = { x: spawn.x, y: spawn.y };
    this.player = new Player(this, spawn.x, spawn.y, this.assets, this.state);
    this.comboMeter = new ComboMeter(this);

    this.titleTag = this.add
      .text(spawn.x, spawn.y - Player.bodyHeight - 36, '', {
        fontFamily: UI_FONT,
        fontSize: '11px',
        color: '#f0d080',
        stroke: '#14141c',
        strokeThickness: 3,
        align: 'center',
        wordWrap: { width: 160, useAdvancedWrap: true },
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.player);
    this.nameTag = this.add
      .text(spawn.x, spawn.y - Player.bodyHeight - 18, '', {
        fontFamily: UI_FONT,
        fontSize: '12px',
        color: TEXT_COLOR.bright,
        stroke: '#14141c',
        strokeThickness: 3,
        align: 'center',
        wordWrap: { width: 160, useAdvancedWrap: true },
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.player);
    this.refreshNameplate();
    this.state.events.on(EV.titleChanged, this.refreshNameplate, this);

    this.physics.add.collider(
      this.player,
      this.terrain.platforms,
      undefined,
      (_player, platform) => {
        const oneWay = (platform as Phaser.GameObjects.Rectangle).getData('oneWay') === true;
        if (!oneWay) return true;
        if (this.player.isDroppingThrough) return false;
        if (this.player.isClimbing) return false;
        return true;
      },
    );

    this.spawner = new SpawnDirector(
      this,
      this.map,
      this.assets,
      (m) => this.onMonsterKilled(m),
      (m, dir) => this.spawnMonsterShot(m, dir),
    );
    this.physics.add.collider(this.spawner.group, this.terrain.platforms);
    this.physics.add.overlap(this.player, this.spawner.group, (_p, m) =>
      this.onContact(m as Monster),
    );
    this.spawner.spawnAll();
    this.spawnNpcs();

    this.drops = this.physics.add.group();
    this.physics.add.collider(this.drops, this.terrain.platforms);

    this.shots = this.physics.add.group();
    this.physics.add.collider(this.shots, this.terrain.platforms, (shot) => {
      shot.destroy();
    });
    this.physics.add.overlap(this.player, this.shots, (_p, shot) => {
      this.onMonsterShot(shot as MonsterShot);
    });

    this.playerShots = this.physics.add.group({ allowGravity: false });
    this.physics.add.overlap(this.playerShots, this.spawner.group, (shot, mob) => {
      this.onPlayerShot(shot as PlayerShot, mob as Monster);
    });

    this.damageNumbers = new DamageNumbers(this, this.assets);
    this.hpBars = this.add.graphics().setDepth(DEPTH.monsterHpBar);

    this.cameras.main.setDeadzone(220, 260);
    this.cameras.main.startFollow(this.player, true, 0.14, 0.14, 0, 40);
    this.cameras.main.fadeIn(280, 0, 0, 0);

    this.actionHint = this.add
      .text(0, 0, '↑', {
        fontFamily: UI_FONT,
        fontSize: '24px',
        color: '#ffffff',
        stroke: '#1b2b3a',
        strokeThickness: 5,
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.damageNumber)
      .setVisible(false);

    this.bindPlayerEvents();
    this.bindDevHelpers();
    this.showMapTitle();
    bgm.play(this.map.bgmMood);

    this.state.events.emit(EV.mapChanged, {
      mapId: this.map.id,
      name: this.map.name,
      subtitle: this.map.subtitle,
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private cleanup(): void {
    this.state.events.off(EV.levelUp, this.onLevelUp, this);
    this.state.events.off(EV.statusTick, this.onStatusTick, this);
    this.state.events.off(EV.reviveChosen, this.onReviveChosen, this);
    this.state.events.off(EV.titleChanged, this.refreshNameplate, this);
    net.events.off(EV.netShareExp, this.onShareExp, this);
    for (const remote of this.remotes.values()) remote.destroy();
    this.remotes.clear();
  }

  private bindPlayerEvents(): void {
    const e = this.player.events;
    e.on(PLAYER_EVENT.jump, () => Sfx.jump());
    e.on(PLAYER_EVENT.land, () => Sfx.land());
    e.on(PLAYER_EVENT.climbStep, () => Sfx.climb());
    e.on(PLAYER_EVENT.attackStart, (skill: SkillDef) => {
      if (skill.mpCost > 0) Sfx.skill();
      else Sfx.swing();
      if (skill.kind === 'melee') this.spawnSlashEffect(skill);
    });
    e.on(PLAYER_EVENT.attackHit, (skill: SkillDef) => this.resolveAttack(skill));
    e.on(PLAYER_EVENT.noMp, (skill: SkillDef) => {
      Sfx.deny();
      this.state.events.emit(EV.toast, { message: `MP가 부족합니다 (${skill.name})`, tone: 'warn' });
    });
    e.on(PLAYER_EVENT.died, () => {
      Sfx.playerDie();
      const lost = this.state.applyDeathPenalty();
      this.deathLostExp = lost;
      this.saveNow();
      this.deathTimer = RESPAWN.playerMs;
      this.deathPromptShown = false;
      this.state.events.emit(EV.toast, {
        message: lost > 0 ? `쓰러졌습니다  (EXP −${lost})` : '쓰러졌습니다',
        tone: 'warn',
      });
    });

    this.state.events.on(EV.levelUp, this.onLevelUp, this);
    this.state.events.on(EV.statusTick, this.onStatusTick, this);
    this.state.events.on(EV.reviveChosen, this.onReviveChosen, this);
    net.events.on(EV.netShareExp, this.onShareExp, this);
    this.input.keyboard?.on('keydown-P', () => this.inviteNearest());
  }

  /**
   * 개발 편의 기능 (dev 빌드에서만 동작).
   * 수동 테스트에서 맵 끝까지 걸어가거나 레벨업까지 사냥하는 시간을 줄이기 위한 것으로,
   * 프로덕션 빌드에는 포함되지 않는다.
   */
  private bindDevHelpers(): void {
    if (!import.meta.env.DEV) return;
    const kb = this.input.keyboard;
    if (!kb) return;

    kb.on('keydown-F9', () => {
      const portal = this.terrain.portals[0];
      if (!portal) return;
      this.player.setPosition(portal.x, portal.y - 10);
      this.state.events.emit(EV.toast, { message: '[dev] 포탈로 이동', tone: 'info' });
    });
    kb.on('keydown-F10', () => {
      this.state.gainExp(80);
      this.state.events.emit(EV.toast, { message: '[dev] 경험치 +640', tone: 'info' });
    });
  }

  private onLevelUp(payload: { level: number }): void {
    Sfx.levelUp();
    const fx = this.add.sprite(this.player.x, this.player.y - 40, 'fx-level-up', 0);
    fx.setDepth(DEPTH.effect).play(ANIM.levelUp);
    fx.on('animationcomplete', () => fx.destroy());

    const text = this.add
      .text(this.player.x, this.player.y - 130, `LEVEL UP!  Lv.${payload.level}`, {
        fontFamily: UI_FONT,
        fontSize: '22px',
        color: TEXT_COLOR.gold,
        stroke: '#3a2a08',
        strokeThickness: 6,
        align: 'center',
        wordWrap: { width: 400, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.damageNumber);
    this.tweens.add({
      targets: text,
      y: text.y - 50,
      alpha: 0,
      duration: 1400,
      ease: 'Quad.Out',
      onComplete: () => text.destroy(),
    });
    this.saveNow();
  }

  /* -------------------------------- 전투 -------------------------------- */

  private spawnSlashEffect(skill: SkillDef): void {
    const key =
      skill.effect === 'blast' ? 'fx-blast' : skill.effect === 'power' ? 'fx-slash-strong' : 'fx-slash';
    const anim =
      skill.effect === 'blast' ? ANIM.blast : skill.effect === 'power' ? ANIM.slashStrong : ANIM.slash;
    const offset = skill.effect === 'blast' ? 10 : 56;
    const fx = this.add.sprite(
      this.player.x + offset * this.player.facing,
      this.player.y - Player.bodyHeight / 2,
      key,
      0,
    );
    fx.setDepth(DEPTH.effect)
      .setFlipX(this.player.facing < 0)
      .setAlpha(0.92)
      .play(anim);
    fx.on('animationcomplete', () => fx.destroy());
  }

  private resolveAttack(skill: SkillDef): void {
    if (skill.kind === 'buff') {
      this.state.applySkillBuff(skill);
      const fx = this.add.sprite(this.player.x, this.player.y - 36, 'fx-level-up', 0);
      fx.setDepth(DEPTH.effect).setScale(0.55).play(ANIM.levelUp);
      fx.on('animationcomplete', () => fx.destroy());
      return;
    }
    if (skill.kind === 'projectile') {
      const hits = Math.max(1, skill.hits);
      for (let i = 0; i < hits; i++) {
        this.time.delayedCall(i * 90, () => {
          if (this.player.mode === 'dead') return;
          const shot = new PlayerShot(
            this,
            this.player.x + this.player.facing * 28,
            this.player.y - 22,
            this.player.facing,
            skill,
          );
          this.playerShots.add(shot);
          shot.arm();
        });
      }
      return;
    }

    const id = ++this.attackId;
    const rect = this.player.hitboxFor(skill);

    const targets = this.spawner
      .alive()
      .filter((m) => {
        if (!m.canBeHitBy(id)) return false;
        const body = m.body as Phaser.Physics.Arcade.Body;
        const mrect = new Phaser.Geom.Rectangle(body.x, body.y, body.width, body.height);
        return Phaser.Geom.Intersects.RectangleToRectangle(rect, mrect);
      })
      .sort(
        (a, b) => Math.abs(a.x - this.player.x) - Math.abs(b.x - this.player.x),
      )
      .slice(0, skill.maxTargets);

    if (targets.length === 0) return;

    this.applyHits(targets, skill, id);
  }

  private onPlayerShot(shot: PlayerShot, monster: Monster): void {
    if (!shot.active || !monster.active || monster.isDying) return;
    const id = ++this.attackId;
    if (!monster.canBeHitBy(id)) return;
    this.applyHits([monster], shot.skill, id);
    shot.consumeHit();
  }

  private applyHits(targets: Monster[], skill: SkillDef, id: number): void {
    const stats = this.state.stats;
    const hitCount = skill.kind === 'projectile' ? 1 : Math.max(1, skill.hits);
    targets.forEach((monster) => {
      for (let hit = 0; hit < hitCount; hit++) {
        const apply = (): void => {
          if (!monster.active || monster.isDying) return;
          if (net.connected && monster.spawnKey) net.claim(monster.spawnKey);
          if (!rollHit(stats.accuracy, monster.def.avoid)) {
            monster.noteAttack(id);
            this.damageNumbers.showMiss(monster.x, monster.centerY - 18);
            this.combo.noteMiss();
            this.comboMeter.set(this.combo.count);
            return;
          }
          const hits = this.combo.noteHit();
          this.comboMeter.set(hits);
          this.state.noteCombo(hits);
          const result = rollPlayerDamage(
            this.state.stats,
            monster.def.defense,
            skill.damageMultiplier * this.state.buffDamageMul * this.combo.damageMul(),
          );
          monster.takeDamage(result.amount, id, this.player.x, skill.knockback);
          training.note({ amount: result.amount, name: monster.def.name, crit: result.crit, toPlayer: false });
          this.damageNumbers.show(
            monster.x + Phaser.Math.Between(-8, 8),
            monster.centerY - 18,
            result.amount,
            result.crit ? 'crit' : 'normal',
          );
          this.spawnHitEffect(monster.x, monster.centerY);
          if (result.crit) Sfx.crit();
          else Sfx.hit();
          this.cameras.main.shake(
            90,
            (result.crit
              ? COMBAT.shake.crit
              : skill.mpCost > 0
                ? COMBAT.shake.skill
                : COMBAT.shake.normal) * settings().shakeMul,
          );
        };
        if (hit === 0) apply();
        else this.time.delayedCall(hit * 110, apply);
      }
    });
  }

  private spawnHitEffect(x: number, y: number): void {
    const fx = this.add.sprite(x, y, 'fx-hit', 0);
    fx.setDepth(DEPTH.effect).setScale(Phaser.Math.FloatBetween(0.8, 1.1)).play(ANIM.hit);
    fx.on('animationcomplete', () => fx.destroy());
  }

  private spawnMonsterShot(monster: Monster, dir: 1 | -1): void {
    const ranged = monster.def.ai?.ranged;
    if (!ranged) return;
    const shot = new MonsterShot(
      this,
      monster.x + dir * 22,
      monster.centerY - 4,
      dir,
      ranged.speed,
      monster.def.accuracy,
      monster.def.attack,
      monster.def.inflict,
    );
    this.shots.add(shot);
    Sfx.mobShot();
  }

  private onMonsterShot(shot: MonsterShot): void {
    if (!shot.active) return;
    if (this.player.isInvulnerable || this.player.mode === 'dead') {
      shot.destroy();
      return;
    }
    const accuracy = shot.accuracy;
    const attack = shot.attack;
    const inflict = shot.inflict;
    shot.destroy();
    if (!rollHit(accuracy, this.state.stats.avoid)) {
      this.damageNumbers.showMiss(this.player.x, this.player.y - Player.bodyHeight - 16);
      return;
    }
    const amount = rollMonsterDamage(attack, this.state.combatDefense);
    if (this.player.takeHit(amount)) {
      this.damageNumbers.show(this.player.x, this.player.y - Player.bodyHeight - 16, amount, 'player');
      training.note({ amount, name: '포자', crit: false, toPlayer: true });
      Sfx.playerHurt();
      this.cameras.main.shake(140, 0.005 * settings().shakeMul);
      this.tryInflict(inflict);
    }
  }

  private onContact(monster: Monster): void {
    if (monster.isDying || !monster.active) return;
    if (this.player.isInvulnerable) return;
    const now = this.time.now;
    if (now < monster.nextContactAt) return;
    monster.nextContactAt = now + COMBAT.contactIntervalMs;

    if (!rollHit(monster.def.accuracy, this.state.stats.avoid)) {
      this.damageNumbers.showMiss(this.player.x, this.player.y - Player.bodyHeight - 16);
      return;
    }

    const amount = rollMonsterDamage(monster.def.attack, this.state.combatDefense);
    if (this.player.takeHit(amount)) {
      this.damageNumbers.show(
        this.player.x,
        this.player.y - Player.bodyHeight - 16,
        amount,
        'player',
      );
      Sfx.playerHurt();
      training.note({ amount, name: monster.def.name, crit: false, toPlayer: true });
      this.cameras.main.shake(140, 0.005 * settings().shakeMul);
      this.tryInflict(monster.def.inflict);
    }
  }

  private onStatusTick(p: StatusTickPayload): void {
    this.damageNumbers.show(
      this.player.x,
      this.player.y - Player.bodyHeight - 16,
      p.amount,
      'player',
    );
  }

  private tryInflict(inflict?: StatusInflict): void {
    if (!inflict) return;
    if (Math.random() >= inflict.chance) return;
    if (this.state.applyStatus(inflict.status)) Sfx.status();
  }

  private onMonsterKilled(monster: Monster): void {
    Sfx.monsterDie();
    if (net.connected && monster.spawnKey && !net.canLoot(monster.spawnKey, performance.now())) {
      this.state.events.emit(EV.toast, { message: '다른 유저가 선점한 몬스터입니다', tone: 'warn' });
      return;
    }
    if (net.connected && monster.spawnKey) {
      net.notifyKill(monster.spawnKey, monster.def.exp, this.map.id, monster.x, monster.y);
    }
    const def = monster.def;
    this.state.gainExp(def.exp);
    this.state.noteKill(def.id);

    const gained = Math.round(def.exp * PROGRESSION.expRate);
    const expText = this.add
      .text(monster.x, monster.centerY - 6, `+${gained} EXP`, {
        fontFamily: UI_FONT,
        fontSize: '13px',
        color: '#c8f0ff',
        stroke: '#14243a',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.damageNumber);
    this.tweens.add({
      targets: expText,
      y: expText.y - 34,
      alpha: 0,
      duration: 900,
      onComplete: () => expText.destroy(),
    });

    if (Math.random() < DROPS.mesoChance) {
      const raw = Phaser.Math.Between(def.meso.min, def.meso.max);
      const amount = Math.max(1, Math.round(raw * PROGRESSION.mesoRate));
      this.createDrop(monster.x, monster.y, { type: 'meso', amount });
    }
    for (const drop of def.drops) {
      if (Math.random() >= drop.chance) continue;
      const count = Phaser.Math.Between(drop.min, drop.max);
      for (let i = 0; i < count; i++) {
        this.createDrop(monster.x, monster.y, { type: 'item', itemId: drop.itemId });
      }
    }
  }

  private createDrop(x: number, y: number, kind: Drop['kind']): void {
    const drop = new Drop(this, x + Phaser.Math.Between(-10, 10), y - 10, kind, this.assets);
    this.drops.add(drop);
  }

  /* ------------------------------ 줍기/포탈 ------------------------------ */

  private lootNearby(): void {
    const children = this.drops.getChildren() as Drop[];
    let picked = 0;
    let mesoGained = 0;
    let full = false;

    for (const drop of children) {
      if (!drop.active) continue;
      if (Phaser.Math.Distance.Between(drop.x, drop.y, this.player.x, this.player.y) >
        PLAYER.lootRange) {
        continue;
      }
      if (drop.kind.type === 'meso') {
        this.state.gainMeso(drop.kind.amount);
        mesoGained += drop.kind.amount;
        drop.destroy();
        picked++;
        continue;
      }
      const left = this.state.inventory.add(drop.kind.itemId, 1);
      if (left > 0) {
        full = true;
        continue;
      }
      this.state.events.emit(EV.toast, {
        message: `${ITEMS[drop.kind.itemId]?.name ?? '아이템'} 획득`,
        tone: 'good',
      });
      drop.destroy();
      picked++;
    }

    if (mesoGained > 0) Sfx.pickupMeso();
    else if (picked > 0) Sfx.pickupItem();
    if (full) {
      this.state.events.emit(EV.toast, { message: '인벤토리가 꽉 찼습니다', tone: 'warn' });
    }
  }

  private spawnNpcs(): void {
    this.npcs = [];
    for (const spawn of this.map.npcs ?? []) {
      this.npcs.push(new Npc(this, spawn.x, spawn.y, spawn.npcId, this.assets));
    }
  }

  private nearNpc(): Npc | undefined {
    return this.npcs.find(
      (npc) => Math.abs(npc.x - this.player.x) <= 48 && Math.abs(npc.y - this.player.y) <= 90,
    );
  }

  private tryNpcTalk(): boolean {
    const npc = this.nearNpc();
    if (!npc) return false;
    this.state.events.emit(EV.npcTalk, { npcId: npc.def.id });
    return true;
  }

  private checkPortal(): void {
    if (this.transitioning) return;
    for (const portal of this.terrain.portals) {
      if (Math.abs(portal.x - this.player.x) > 46) continue;
      if (Math.abs(portal.y - this.player.y) > 90) continue;
      this.usePortal(portal.targetMap, portal.targetSpawn);
      return;
    }
  }

  private usePortal(mapId: string, spawnId: string): void {
    if (!MAPS[mapId]) return;
    this.transitioning = true;
    Sfx.portal();
    this.saveNow(mapId, spawnId);
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.start('play', { mapId, spawnId });
    });
  }

  private onReviveChosen(p: { place: 'here' | 'town' }): void {
    if (this.player.mode !== 'dead') return;
    this.deathPromptShown = false;
    this.deathTimer = 0;
    this.state.respawn(RESPAWN.playerHpRatio);

    if (p.place === 'town') {
      const mapId = RESPAWN.townMapId;
      const spawnId = RESPAWN.townSpawnId;
      if (this.map.id !== mapId) {
        this.usePortal(mapId, spawnId);
        return;
      }
      const spawn = this.map.spawns[spawnId] ?? this.spawnPoint;
      this.spawnPoint = { x: spawn.x, y: spawn.y };
      this.state.spawnId = spawnId;
      this.player.revive(spawn.x, spawn.y);
    } else {
      this.player.revive(this.spawnPoint.x, this.spawnPoint.y);
    }

    this.state.events.emit(EV.toast, { message: '부활했습니다', tone: 'info' });
    this.saveNow();
  }

  private showMapTitle(): void {
    const cam = this.cameras.main;
    const title = this.add
      .text(cam.width / 2, 180, this.map.name, {
        fontFamily: UI_FONT,
        fontSize: '40px',
        color: '#ffffff',
        stroke: '#1b2b3a',
        strokeThickness: 8,
        align: 'center',
        wordWrap: { width: cam.width - 80, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH.mapTitle);
    const sub = this.add
      .text(cam.width / 2, 226, this.map.subtitle, {
        fontFamily: UI_FONT,
        fontSize: '16px',
        color: '#e8f4ff',
        stroke: '#1b2b3a',
        strokeThickness: 5,
        align: 'center',
        wordWrap: { width: cam.width - 80, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH.mapTitle);

    this.tweens.add({
      targets: [title, sub],
      alpha: 0,
      delay: 1500,
      duration: 700,
      onComplete: () => {
        title.destroy();
        sub.destroy();
      },
    });
  }

  /* ------------------------------- 저장/루프 ------------------------------- */

  private saveNow(mapId = this.map.id, spawnId = this.state.spawnId): void {
    const data = this.state.toJSON();
    data.mapId = mapId;
    data.spawnId = spawnId;
    SaveGame.save(data);
    if (net.connected) net.pushSave(data);
  }

  /** 사다리/포탈 앞에 서면 머리 위에 ↑ 를 띄운다 (원작의 조작 유도) */
  private updateActionHint(): void {
    if (this.player.isClimbing) {
      this.actionHint.setVisible(false);
      return;
    }
    const climbable = this.player.nearClimbable;
    const portal = this.terrain.portals.find(
      (p) => Math.abs(p.x - this.player.x) <= 46 && Math.abs(p.y - this.player.y) <= 90,
    );
    const npc = this.nearNpc();
    const target = climbable ?? portal ?? npc;
    if (!target) {
      this.actionHint.setVisible(false);
      return;
    }
    this.actionHint
      .setVisible(true)
      .setPosition(this.player.x, this.player.y - Player.bodyHeight - 54);
  }

  private drawMonsterHpBars(): void {
    this.hpBars.clear();
    const now = this.time.now;
    for (const monster of this.spawner.alive()) {
      if (monster.hpBarUntil < now) continue;
      const ratio = Phaser.Math.Clamp(monster.hp / monster.def.maxHp, 0, 1);
      const w = 46;
      const x = monster.x - w / 2;
      const y = monster.y - monster.def.body.height - 16;
      this.hpBars.fillStyle(0x14141c, 0.85);
      this.hpBars.fillRect(x - 1, y - 1, w + 2, 7);
      this.hpBars.fillStyle(0xf24949, 1);
      this.hpBars.fillRect(x, y, w * ratio, 5);
    }
  }

  private refreshNameplate = (): void => {
    const title = getTitle(this.state.titleId);
    this.titleTag.setText(title.name);
    this.titleTag.setColor(title.color);
    this.nameTag.setText(this.state.name);
  };

  private layoutNameplate(): void {
    const x = this.player.x;
    const top = this.player.y - Player.bodyHeight;
    this.titleTag.setPosition(x, top - 34);
    this.nameTag.setPosition(x, top - 16);
  }

  override update(_time: number, delta: number): void {
    this.combo.tick(delta);
    this.comboMeter.set(this.combo.count);
    let input = this.controls.snapshot();
    if (replay.playing) {
      const rec = replay.next();
      if (rec) input = rec;
    } else if (replay.recording) {
      replay.push(delta, input);
    }
    this.state.update(delta);
    this.layoutNameplate();

    if (this.player.mode === 'dead') {
      if (!this.deathPromptShown) {
        this.deathTimer -= delta;
        if (this.deathTimer <= 0) {
          this.deathPromptShown = true;
          this.state.events.emit(EV.deathPrompt, {
            lostExp: this.deathLostExp,
            exp: this.state.exp,
            need: this.state.expNeeded,
          });
        }
      }
      this.spawner.update(this.time.now);
      this.drawMonsterHpBars();
      return;
    }

    this.player.update(delta, input, this.terrain.climbables);
    this.player.clampToWorld(this.map.height);
    this.syncNet(delta);
    for (const monster of this.spawner.alive()) {
      monster.setTarget(this.player.x, this.player.y);
    }

    const hud = this.scene.get('hud') as HudScene | undefined;
    if (hud?.isDialogOpen()) {
      if (input.skill1Pressed) hud.dialogKey('ONE');
      if (input.skill2Pressed) hud.dialogKey('TWO');
      if (input.skill3Pressed) hud.dialogKey('THREE');
      if (input.attackPressed || input.jumpPressed) hud.dialogKey('ENTER');
    } else {
      if (input.attackPressed) this.tryPlayerSkill(applyWeaponToSkill(this.state.basicAttack(), this.state.weapon));
      if (input.skill1Pressed) this.tryQuickSkill(0);
      if (input.skill2Pressed) this.tryQuickSkill(1);
      if (input.skill3Pressed) this.tryQuickSkill(2);
    }
    if (input.lootPressed) this.lootNearby();
    if (input.upPressed) {
      if (!this.tryNpcTalk()) this.checkPortal();
    }

    this.spawner.update(this.time.now);
    this.drawMonsterHpBars();
    this.updateActionHint();

    this.saveTimer += delta;
    if (this.saveTimer > 15000) {
      this.saveTimer = 0;
      this.saveNow();
    }
  }

  private tryQuickSkill(index: number): void {
    const skill = this.state.quickSkill(index);
    if (!skill) {
      this.state.events.emit(EV.toast, { message: '스킬을 아직 배우지 않았습니다 (K)', tone: 'warn' });
      Sfx.deny();
      return;
    }
    this.tryPlayerSkill(applyWeaponToSkill(skill, this.state.weapon));
  }

  private tryPlayerSkill(skill: SkillDef): void {
    this.player.tryAttack(skill);
  }

  private onShareExp(p: { amount: number; from: string }): void {
    this.state.gainExp(p.amount);
    this.state.events.emit(EV.toast, { message: `${p.from} 파티 경험치 +${p.amount}`, tone: 'good' });
  }

  private inviteNearest(): void {
    if (!net.connected) return;
    let best: RemotePlayer | null = null;
    let bestD = 96;
    for (const remote of this.remotes.values()) {
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, remote.sprite.x, remote.sprite.y);
      if (d < bestD) {
        bestD = d;
        best = remote;
      }
    }
    if (!best) {
      this.state.events.emit(EV.toast, { message: '근처에 초대할 유저가 없습니다', tone: 'warn' });
      return;
    }
    net.invite(best.name);
  }

  private syncNet(delta: number): void {
    if (!net.connected) {
      for (const remote of this.remotes.values()) remote.destroy();
      this.remotes.clear();
      return;
    }
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    net.tickPose(delta, {
      mapId: this.map.id,
      x: this.player.x,
      y: this.player.y,
      vx: body.velocity.x,
      vy: body.velocity.y,
      facing: this.player.facing,
      mode: this.player.mode,
    });
    const corr = net.consumeCorrection();
    if (corr) {
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, corr.x, corr.y);
      if (d > NET.correctPx) {
        this.player.x += (corr.x - this.player.x) * 0.35;
        this.player.y += (corr.y - this.player.y) * 0.35;
      }
    }
    const seen = new Set<string>();
    for (const pose of net.others) {
      if (pose.mapId !== this.map.id) continue;
      seen.add(pose.id);
      const existing = this.remotes.get(pose.id);
      if (existing) existing.sync(pose);
      else this.remotes.set(pose.id, new RemotePlayer(this, pose));
    }
    for (const [id, remote] of this.remotes) {
      if (!seen.has(id)) {
        remote.destroy();
        this.remotes.delete(id);
      } else {
        remote.update();
      }
    }
  }

  toggleRecord(): void {
    if (replay.recording) {
      const tape = replay.stopRecord();
      this.state.events.emit(EV.toast, {
        message: tape ? `녹화 끝 ${tape.frames.length}프레임 · JSON 저장` : '녹화 취소',
        tone: 'info',
      });
      replay.download();
      return;
    }
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    replay.beginRecord({
      mapId: this.map.id,
      spawnId: this.state.spawnId,
      x: this.player.x,
      y: this.player.y,
      vx: body.velocity.x,
      vy: body.velocity.y,
    });
    this.state.events.emit(EV.toast, { message: '입력 녹화 시작 (F7 종료)', tone: 'info' });
  }

  playLast(): void {
    const tape = replay.beginPlay();
    if (!tape) {
      this.state.events.emit(EV.toast, { message: '재생할 녹화가 없습니다', tone: 'warn' });
      return;
    }
    if (tape.mapId !== this.map.id) {
      replay.playing = false;
      this.state.events.emit(EV.toast, { message: '같은 맵에서만 재생됩니다', tone: 'warn' });
      return;
    }
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    this.player.x = tape.x;
    this.player.y = tape.y;
    body.setVelocity(tape.vx, tape.vy);
    this.state.events.emit(EV.toast, { message: '입력 재생', tone: 'info' });
  }
}
